<script setup>
import { computed } from "vue"
import { Link } from "@inertiajs/vue3"
import Spinner from "@/components/feedback/Spinner.vue"

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (v) => ["primary", "secondary", "outline", "ghost", "danger", "link"].includes(v),
  },
  size: {
    type: String,
    default: "md",
    validator: (v) => ["sm", "md", "lg"].includes(v),
  },
  type: { type: String, default: "button" },
  href: { type: String, default: null },
  method: { type: String, default: "get" },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  block: { type: Boolean, default: false },
})

const variantClasses = {
  primary: "bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary",
  secondary:
    "bg-secondary text-black hover:bg-secondary/80 focus-visible:ring-primary",
  outline:
    "border-2 border-primary bg-white text-primary hover:bg-primary/5 focus-visible:ring-primary",
  ghost: "bg-transparent text-black hover:bg-black/5 focus-visible:ring-primary",
  danger: "bg-rose-600 text-white hover:bg-rose-500 focus-visible:ring-rose-600",
  link: "bg-transparent text-primary hover:text-primary/80 underline underline-offset-2 focus-visible:ring-primary px-0",
}

const sizeClasses = {
  sm: "h-8 text-xs px-3",
  md: "h-10 text-sm px-4",
  lg: "h-12 text-base px-5",
}

const linkSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
}

const classes = computed(() => [
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium",
  "transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
  "disabled:opacity-60 disabled:cursor-not-allowed",
  variantClasses[props.variant],
  props.variant === "link" ? linkSizeClasses[props.size] : sizeClasses[props.size],
  props.block && "w-full",
])
</script>

<template>
  <Link
    v-if="href"
    :href="href"
    :method="method"
    :class="classes"
  >
    <Spinner v-if="loading" />
    <slot />
  </Link>
  <button
    v-else
    :type="type"
    :disabled="disabled || loading"
    :class="classes"
  >
    <Spinner v-if="loading" />
    <slot />
  </button>
</template>
